import {
  IonButton,
  IonCol,
  IonContent,
  IonGrid,
  IonModal,
  IonRow,
} from "@ionic/react";
import { Editor } from "@tinymce/tinymce-react";
import { API_URL, TINYMCE_API_KEY } from "../../actions/settings";
import { document_icon } from "../../AE2/static/icons/document_icon";
import { red_trashcan } from "../../AE2/static/icons/red_trashcan";
import { save_icon } from "../../AE2/static/icons/save_icon";
import Ae2Header from "../../components/AE2/Ae2Header";
import { useEffect, useRef, useState } from "react";
import NotificationPopUp from "../../components/extras/NotificationPopUp";
import S3Bucket from "../../components/extras/S3Bucket";
import { useAppSelector } from "../../Hooks";
import { selectUser } from "../../reducers/UserReducer";
import { Redirect, useParams } from "react-router";
import { ReportCSS } from "../../components/AE2/CostAvoidanceReport/ReportCSS";

async function saveTemplate( token: any, id:string, data: any) {
  return fetch(`${API_URL}/cost_avoidance/template/${id}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: token,
    },
    body: JSON.stringify(data),
  }).then((data) => data.json());
}

async function deleteTemplate(token: any, id: any) {
  return fetch(`${API_URL}/cost_avoidance/template/${id}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: token,
    }
  }).then((data) => data.json());
}


const EditCostAvoidance: React.FC = () => {
  const user=useAppSelector(selectUser)
  const { id } = useParams<{ id: string }>();
  const editorRef = useRef<any>(null);
  const trashRef = useRef<any>(null);
  const [defaultTemplate, setDefaultTemplate] = useState<string>("");
  const [showDeleteModal, setDeleteModal] = useState<boolean>(false);
  const styles = ReportCSS;

  useEffect(() => {

    const loadDefault = async () => {
      const html_file: any = await S3Bucket.GetFile('cost_avoidance_reports/costAvoidanceReport.html',null);
      const html_string = await html_file?.text();
      
      
      setDefaultTemplate(html_string)
      try {
        const html_file: any = await S3Bucket.GetFile(`cost_avoidance_reports/${id}_costAvoidanceReport.html`,null);
        editorRef.current.contentDocument.body.innerHTML =
        await html_file?.text();
        trashRef.current.setEnabled(true);
      }
      catch {
        editorRef.current.contentDocument.body.innerHTML = html_string;
      }
    }
    loadDefault();
  }, [id]);


  const handleDelete = async () => {
    const token = localStorage.getItem("token") || "";
    await deleteTemplate(`Token ${token.slice(1, -1)}`, id);

    handleNewDocument();
    setDeleteModal(false);
  };


  const handleSaveTemplate= async () => {
    const data = {
      template: editorRef.current.contentDocument.body.innerHTML,
    };
    const token = localStorage.getItem("token") || "";
    const res = await saveTemplate(
      `Token ${token.slice(1, -1)}`,
      id,
      data
    );
    if (res.status === "success" || res.status === "Success") {
      NotificationPopUp("success", res.detail);
    } else {
      NotificationPopUp("error", res.detail);
    }
  };

  const handleNewDocument = () => {
    trashRef.current.setEnabled(false);
    editorRef.current.setContent(defaultTemplate);
  };

  const confirmDelete = async () => {
    await handleDelete();
    setDeleteModal(false);
  };

  if(user.first_name !=='' && !user.groups.includes('CostAvoidanceTemplateEditDelete')){
    return <Redirect to="/403"/>
  }

  return (
    <IonContent className="estar-template">
      <Ae2Header />
      <IonRow className="ion-justify-content-center" style={{ height: "100%" }}>
        <Editor
          apiKey={TINYMCE_API_KEY}
          initialValue={''}
          onInit={(evt, editor) => (editorRef.current = editor)}
          init={{
            height: "100%",
            width: "1000px",
            plugins: "save lists",
            editable_root: true,        
            newdocument_content: defaultTemplate,
            toolbar:
              "undo redo | formatselect | " +
              "bold italic backcolor | alignleft aligncenter " +
              "alignright alignjustify | bullist numlist outdent indent | " +
              "removeformat |  save trash",
            menu: {
              file: {
                title: "File",
                items: "newdoc ",
              },
              variables: {
                title: "Variables",
                items: "annual_cost cars cost_avoidance electrical_savings gas_savings building_incentive ordinal score",
              },
              functions: {
                title: "Functions",
                items: "if",
              },
            },
            menubar: "file edit view insert format variables functions",
            content_style: styles,
            save_enablewhendirty: false,
            save_onsavecallback: (e: any) => {
              handleSaveTemplate();
            },
            setup: function (editor) {
              editor.ui.registry.addIcon("new_doc", document_icon);
              editor.ui.registry.addIcon("trash", red_trashcan);              
              editor.ui.registry.addIcon("save", save_icon);              
              editor.ui.registry.addToggleMenuItem("newdoc", {
                text: "New Document",
                icon: "new_doc",
                onAction: () => handleNewDocument(),
              });
              editor.ui.registry.addButton("trash", {
                enabled: false,
                icon: "trash",
                tooltip: "Delete Changes",
                onAction: () => setDeleteModal(true),
                onSetup: (api) => {
                  trashRef.current = api;
                  return () => {};
                },
              });
              editor.ui.registry.addMenuItem('score', {
               text: 'Score',
               onAction: function () {
                 editor.insertContent('{{score}}');
               }
              });
              editor.ui.registry.addMenuItem('cost_avoidance', {
               text: 'Cost Avoidance',
               onAction: function () {
                 editor.insertContent('{{cost_avoidance|intcomma}}');
               }
              });
              editor.ui.registry.addMenuItem('electrical_savings', {
               text: 'Electrical Savings',
               onAction: function () {
                 editor.insertContent('{{electrical savings}}');
               }
              });
              editor.ui.registry.addMenuItem('gas_savings', {
               text: 'Gas Savings',
               onAction: function () {
                 editor.insertContent('{{gas_savings}}');
               }
              });
              editor.ui.registry.addMenuItem('cars', {
               text: 'Cars',
               onAction: function () {
                 editor.insertContent('{{cars}}');
               }
              });
              editor.ui.registry.addMenuItem('annual_cost', {
               text: 'Annual Cost',
               onAction: function () {
                 editor.insertContent('{{annual_cost|intcomma}}');
               }
              });
              editor.ui.registry.addMenuItem('building_incentive', {
               text: 'Incentives',
               onAction: function () {
                 editor.insertContent('{{ building.building_incentive|intcomma }}');
               }
              });
              editor.ui.registry.addMenuItem('ordinal', {
               text: 'Ordinal Percentile',
               onAction: function () {
                 editor.insertContent('{{ ordinal }}');
               }
              });
              editor.ui.registry.addMenuItem('if', {
               text: 'if else',
               onAction: function () {
                 editor.insertContent('{% if something %}                                     {% else %}                                    {% endif %}');
               }
              });
            },
          }}
        />
      </IonRow>

      <IonModal
        isOpen={showDeleteModal}
        onDidDismiss={() => setDeleteModal(false)}
        className="confirmDelete"
      >
        <IonGrid>
          <IonRow className="ion-justify-content-center">
            <IonCol className="delete-warning">
              <span>WARNING:</span> Deleting the cost avoidance report changes is
              permanent and cannot be undone.
            </IonCol>
          </IonRow>
          <IonRow className="ion-justify-content-center">
            <IonButton color="danger" onClick={() => confirmDelete()}>
              Delete
            </IonButton>
            <IonButton color="medium" onClick={() => setDeleteModal(false)}>
              Cancel
            </IonButton>
          </IonRow>
        </IonGrid>
      </IonModal>
    </IonContent>
  );
};

export default EditCostAvoidance;
